import React from 'react';

import setFavoriteContent from '@smart-tv/utils/kmm/setFavoriteContent';
import type { Payload as SetFavoriteContentPayload } from '@smart-tv/utils/kmm/setFavoriteContent';
import useGetChannels from '@smart-tv/pages/tv-guide/utils/use-get-channels';

interface FavoritedContent {
  contentId: number;
  contentType: SetFavoriteContentPayload['contentType'];
}

interface FavoriteContentParams {
  id: number;
  type: SetFavoriteContentPayload['contentType'];
  title: string;
  category?: string;
}

export interface FavoritedContentContextProps {
  children: React.ReactNode | React.ReactNode[];
}

export interface FavoritedContentContextValues {
  favoritedContent?: FavoritedContent;
  unfavoritedContent?: FavoritedContent;
  favoriteContent: (params: FavoriteContentParams) => Promise<boolean>;
  unfavoriteContent: (params: FavoriteContentParams) => Promise<boolean>;
}

export const FavoritedContentContext = React.createContext<FavoritedContentContextValues>(
  {} as FavoritedContentContextValues
);

export const FavoritedContentConsumer = FavoritedContentContext.Consumer;

export const FavoritedContentProvider = (props: FavoritedContentContextProps): JSX.Element => {
  const { children } = props;

  const [favoritedContent, setFavoritedContent] = React.useState<FavoritedContent>();

  const [unfavoritedContent, setUnfavoritedContent] = React.useState<FavoritedContent>();

  const { updateChannelsForNewFavorite } = useGetChannels();

  const favoriteContent = React.useCallback(async (params: FavoriteContentParams) => {
    const { id: contentId, type: contentType } = params;

    const payload = { contentId, contentType, setContentActive: true };
    const response = await setFavoriteContent(payload);

    setFavoritedContent({ contentId, contentType });

    return response;
  }, []);

  const unfavoriteContent = React.useCallback(async (params: FavoriteContentParams) => {
    const { id: contentId, type: contentType } = params;

    const payload = { contentId, contentType, setContentActive: false };
    const response = await setFavoriteContent(payload);

    setUnfavoritedContent({ contentId, contentType });

    return response;
  }, []);

  /** Set it back to undefined if set, since we only want to fire and forget */
  React.useEffect(() => {
    if (favoritedContent) {
      setFavoritedContent(undefined);
      updateChannelsForNewFavorite(true, favoritedContent.contentId, favoritedContent.contentType);
    }
  }, [favoritedContent, updateChannelsForNewFavorite]);

  /** Set it back to undefined if set, since we only want to fire and forget */
  React.useEffect(() => {
    if (unfavoritedContent) {
      setUnfavoritedContent(undefined);
      updateChannelsForNewFavorite(
        false,
        unfavoritedContent.contentId,
        unfavoritedContent.contentType
      );
    }
  }, [unfavoritedContent, updateChannelsForNewFavorite]);

  return (
    <FavoritedContentContext.Provider
      value={{ favoriteContent, unfavoriteContent, favoritedContent, unfavoritedContent }}
    >
      {children}
    </FavoritedContentContext.Provider>
  );
};

export default FavoritedContentContext;
